import { debounce } from 'lodash';
import { datesEqual, formatDateIso, getMonth, getMonths } from '@helpers';
import { BaseComponent } from '../base.component';
import defaultHtml from './date-picker.component.hbs';

interface CalendarDay {
    date: Date;
    value: string;
    currentMonth: boolean;
    enabled: boolean;
    selected: boolean;
}

export class DatePickerComponent extends BaseComponent<never> {
    static get observedAttributes(): Array<string> {
        return ['max', 'min', 'value'];
    }

    protected override bindEvent = undefined;
    protected override customTemplatePropertyName = 'datePicker';
    protected override defaultHtml = defaultHtml;

    private minDate?: Date;
    private maxDate?: Date;
    private selectedDate?: Date;
    private modalVisible = false;
    private modalDate = new Date();
    private monthSelectorVisible = false;
    private documentClickEventHandler!: (event: MouseEvent) => void;
    private windowResizeEventHandler!: (event: Event) => void;

    override connectedCallback(): void {
        super.connectedCallback();

        this.documentClickEventHandler = (event: MouseEvent): void => {
            if (!this.modalVisible || event.composedPath().includes(this)) {
                return;
            }

            this.modalVisible = false;

            setTimeout(() => this.renderContent());
        };

        document.addEventListener('click', this.documentClickEventHandler);

        this.windowResizeEventHandler = debounce((event: Event): void => {
            this.positionModal();
        }, 100);

        window.addEventListener('resize', this.windowResizeEventHandler);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        document.removeEventListener('click', this.documentClickEventHandler);
        window.addEventListener('resize', this.windowResizeEventHandler);
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
        const date = newValue ? new Date(newValue) : undefined;

        if (date) {
            date.setHours(0, 0, 0, 0);
        }

        switch (name) {
            case 'max':
                this.maxDate = date;
                break;
            case 'min':
                this.minDate = date;
                break;
            case 'value':
                this.selectedDate = date;
                break;
        }

        this.renderContent();
    }

    protected override registerHelpers(): void {
        super.registerHelpers();

        this.handlebars.registerHelper('dayOfMonth', (date: Date): string => {
            return date.getDate().toString().padStart(2, '0');
        });
    }

    protected getContentHtml(template: HandlebarsTemplateDelegate): string {
        const html = template({
            value: this.selectedDate ? formatDateIso(this.selectedDate) : '',
            modalVisible: this.modalVisible,
            monthSelectorVisible: this.monthSelectorVisible,
            heading: `${getMonth(this.modalDate)} ${this.modalDate.getFullYear()}`,
            years: this.getYears(),
            months: getMonths(),
            weeks: this.getWeeks(this.modalDate),
            currentYear: this.modalDate.getFullYear(),
            currentMonth: getMonth(this.modalDate)
        });

        return html;
    }

    protected bindChildElements(): void {
        this.positionModal();

        this.shadowRoot?.querySelectorAll('[hawksearch-input]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                this.modalVisible = true;
                this.modalDate = this.selectedDate ? new Date(this.selectedDate) : new Date();

                this.renderContent();
            });
        });

        this.shadowRoot?.querySelectorAll('[hawksearch-previous]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                this.modalDate.setDate(1);
                this.modalDate.setMonth(this.modalDate.getMonth() - 1);

                this.renderContent();
            });
        });

        this.shadowRoot?.querySelectorAll('[hawksearch-next]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                this.modalDate.setDate(1);
                this.modalDate.setMonth(this.modalDate.getMonth() + 1);

                this.renderContent();
            });
        });

        this.shadowRoot?.querySelectorAll('[hawksearch-month-selector]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                this.monthSelectorVisible = true;

                this.renderContent();
            });
        });

        this.shadowRoot?.querySelectorAll('[hawksearch-select-month]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                const month = (this.shadowRoot?.querySelector('[hawksearch-month]') as HTMLSelectElement | null)?.value;
                const year = (this.shadowRoot?.querySelector('[hawksearch-year]') as HTMLSelectElement | null)?.value;

                if (month === undefined) {
                    throw new Error('Unable to get month value');
                }

                if (year === undefined) {
                    throw new Error('Unable to get year value');
                }

                this.modalDate = new Date(parseInt(year), parseInt(month), 1);
                this.monthSelectorVisible = false;

                this.renderContent();
            });
        });

        this.shadowRoot?.querySelectorAll('[hawksearch-date]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                const value = e.getAttribute('hawksearch-date') || undefined;

                if (!value) {
                    return;
                }

                const date = new Date(value);

                if (!((!this.minDate || date >= this.minDate) && (!this.maxDate || date <= this.maxDate))) {
                    return;
                }

                this.selectedDate = date;
                this.modalVisible = false;

                this.renderContent();

                const newEvent = new CustomEvent('hawksearch:date-picker-changed', {
                    detail: date
                });

                this.dispatchEvent(newEvent);
            });
        });
    }

    private positionModal(): void {
        const modal = this.shadowRoot?.querySelector('[hawksearch-modal]') as HTMLElement;

        if (!modal || !modal.offsetWidth || !this.modalVisible) {
            return;
        }

        modal.style.left = '';
        modal.style.right = '';

        const windowWidth = window.innerWidth;
        const modalBoundaries = modal.getBoundingClientRect();

        if (modal.offsetWidth + modalBoundaries.x >= windowWidth && windowWidth >= modal.offsetWidth) {
            modal.style.right = '0';
        }
    }

    private getYears(): Array<number> {
        const years: Array<number> = [];
        const currentYear = this.modalDate.getFullYear();
        const minYear = currentYear < 1900 ? currentYear : 1900;
        const maxYear = (currentYear > new Date().getFullYear() ? currentYear : new Date().getFullYear()) + 10;

        for (let i = minYear; i <= maxYear; i++) {
            years.push(i);
        }

        return years;
    }

    private getWeeks(date: Date): Array<Array<CalendarDay>> {
        let startDate = new Date(date.getFullYear(), date.getMonth(), 1);

        // Set date to first day of week containing first of month
        startDate.setDate(startDate.getDate() - startDate.getDay());

        const weeks: Array<Array<CalendarDay>> = [];

        do {
            const week = this.getWeek(startDate);

            weeks.push(week);

            startDate = new Date(weeks[weeks.length - 1][6].date.getTime());
            startDate.setDate(startDate.getDate() + 1);
        } while (weeks.length < 6);

        return weeks;
    }

    private getWeek(startDate: Date): Array<CalendarDay> {
        const week: Array<any> = [];
        const today = new Date();

        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < 7; i++) {
            const date = new Date(startDate.getTime());

            date.setDate(date.getDate() + i);
            date.setHours(0, 0, 0, 0);

            const day: CalendarDay = {
                date: date,
                value: date.toISOString(),
                currentMonth: date.getMonth() === this.modalDate.getMonth(),
                enabled: (!this.minDate || date >= this.minDate) && (!this.maxDate || date <= this.maxDate),
                selected: this.selectedDate ? datesEqual(date, this.selectedDate) : false
            };

            week.push(day);
        }

        return week;
    }
}
